export const JST_TIME_ZONE = "Asia/Tokyo"

type TemporalShim = typeof import("temporal-polyfill/shim")
export type TemporalShimLoader = () => Promise<TemporalShim>

const loadTemporalShim: TemporalShimLoader = () =>
  import("temporal-polyfill/shim")

/** Ensure Temporal is available before application or custom-element rendering. */
export async function ensureTemporal(
  loader: TemporalShimLoader = loadTemporalShim,
): Promise<"native" | "polyfill"> {
  if (typeof globalThis.Temporal !== "undefined") return "native"

  const { install } = await loader()
  install()
  if (typeof globalThis.Temporal === "undefined") {
    throw new Error("Temporal installation failed")
  }
  return "polyfill"
}

// Instant.toString({ fractionalSecondDigits: 3 }) が出す正準形。
// UTC固定のZ終端で、小数秒はちょうど3桁になる。
const CANONICAL_INSTANT_PATTERN =
  /^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2}):(\d{2})\.\d{3}Z$/u

/**
 * 値が正準形のInstant文字列かどうかを、Temporalの導入を待たずに判定する。
 *
 * `Temporal.Instant.from(value).toString({ fractionalSecondDigits: 3 }) === value`
 * と同じ判定を、TemporalにもDateにも依存せず行う。bootstrapが板metadataの
 * 検証のためだけにTemporalの導入を待つのを避けるために独立させている。
 */
export function isCanonicalInstantString(value: unknown): value is string {
  if (typeof value !== "string") return false

  const match = CANONICAL_INSTANT_PATTERN.exec(value)
  if (match === null) return false

  const year = Number(match[1])
  const month = Number(match[2])
  const day = Number(match[3])
  const hour = Number(match[4])
  const minute = Number(match[5])
  const second = Number(match[6])

  if (month < 1 || month > 12) return false
  if (day < 1 || day > daysInMonth(year, month)) return false
  // 正準形は閏秒を出さないため、60は受け付けない。
  return hour <= 23 && minute <= 59 && second <= 59
}

function daysInMonth(year: number, month: number): number {
  if (month === 2) return isLeapYear(year) ? 29 : 28
  return month === 4 || month === 6 || month === 9 || month === 11 ? 30 : 31
}

function isLeapYear(year: number): boolean {
  return (year % 4 === 0 && year % 100 !== 0) || year % 400 === 0
}

export function nowInstant(): Temporal.Instant {
  return Temporal.Now.instant()
}

export function nowEpochMilliseconds(): number {
  return nowInstant().epochMilliseconds
}

export function nowIsoUtc(): string {
  return nowInstant().toString({ fractionalSecondDigits: 3 })
}

export function instantFromEpochMilliseconds(
  epochMilliseconds: number,
): Temporal.Instant {
  return Temporal.Instant.fromEpochMilliseconds(epochMilliseconds)
}

export function instantFromIso(value: string): Temporal.Instant | null {
  try {
    return Temporal.Instant.from(value)
  } catch {
    return null
  }
}

export function instantFromJstFields(
  fields: Temporal.DateTimeLikeObject,
): Temporal.Instant | null {
  try {
    return Temporal.PlainDateTime.from(fields, { overflow: "reject" })
      .toZonedDateTime(JST_TIME_ZONE)
      .toInstant()
  } catch {
    return null
  }
}

export function toJst(instant: Temporal.Instant): Temporal.ZonedDateTime {
  return instant.toZonedDateTimeISO(JST_TIME_ZONE)
}

export function formatJstDateTime(
  instant: Temporal.Instant,
  options: Intl.DateTimeFormatOptions,
  locales: Intl.LocalesArgument = "ja-JP",
): string {
  return toJst(instant).toLocaleString(locales, options)
}

export function currentTimeZoneId(): string {
  return Temporal.Now.timeZoneId()
}

/** Match Date#getTimezoneOffset sign and minute units for fingerprint stability. */
export function currentTimeZoneOffsetMinutes(): number {
  const offsetNanoseconds = Temporal.Now.zonedDateTimeISO().offsetNanoseconds
  return -(offsetNanoseconds / 60_000_000_000)
}

/** RFC 7231 cookie dates still require the legacy Date boundary. */
export function toHttpDate(epochMilliseconds: number): string {
  return new Date(epochMilliseconds).toUTCString()
}

/** RFC 7231 Retry-After dates still require the legacy Date boundary. */
export function epochMillisecondsFromHttpDate(value: string): number | null {
  const epochMilliseconds = Date.parse(value)
  return Number.isFinite(epochMilliseconds) ? epochMilliseconds : null
}
